import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export interface Caso {
  id: string
  status: string
  empreendimento: string | null
  construtora: string | null
  valor_entrada: number | null
  created_at: string
}

export interface EventoProcesso {
  id: string
  tipo: string | null
  descricao: string | null
  data_evento: string
}

export interface PagamentoCliente {
  id: string
  tipo: string
  valor: number
  status: string
  vencimento: string | null
  link_pagamento: string | null
}

// Carrega o caso do cliente logado + timeline + cobranças, e escuta mudanças via Realtime.
export function useRealtimeCaso() {
  const [caso, setCaso] = useState<Caso | null>(null)
  const [eventos, setEventos] = useState<EventoProcesso[]>([])
  const [pagamentos, setPagamentos] = useState<PagamentoCliente[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  async function carregar() {
    const { data: casos, error: e1 } = await supabase
      .from('casos')
      .select('id, status, empreendimento, construtora, valor_entrada, created_at')
      .order('created_at', { ascending: false })
      .limit(1)
    if (e1) {
      setErro(e1.message)
      setLoading(false)
      return
    }
    const c = (casos?.[0] as Caso) ?? null
    setCaso(c)
    if (c) {
      const [{ data: evs, error: e2 }, { data: pgs, error: e3 }] = await Promise.all([
        supabase
          .from('eventos_processo')
          .select('id, tipo, descricao, data_evento')
          .eq('caso_id', c.id)
          .order('data_evento', { ascending: false }),
        supabase
          .from('pagamentos')
          .select('id, tipo, valor, status, vencimento, link_pagamento')
          .eq('caso_id', c.id)
          .order('criado_em', { ascending: true }),
      ])
      if (e2 || e3) {
        setErro((e2 ?? e3)!.message)
        setLoading(false)
        return
      }
      setEventos((evs as EventoProcesso[]) ?? [])
      setPagamentos((pgs as PagamentoCliente[]) ?? [])
    } else {
      setEventos([])
      setPagamentos([])
    }
    setErro(null)
    setLoading(false)
  }

  useEffect(() => {
    carregar()
    const ch = supabase
      .channel('caso-cliente')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'casos' }, () => carregar())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'eventos_processo' }, () => carregar())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pagamentos' }, () => carregar())
      .subscribe()
    return () => {
      supabase.removeChannel(ch)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { caso, eventos, pagamentos, loading, erro, recarregar: carregar }
}
